import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  fetchPendingLicenses,
  decideLicense,
} from "../../../api/customAdmin/customAdminService";
import ModalNew from "../../../components/ui/popup/Modal";
import PopupHeader from "../../../components/ui/popup/common/popupHeader";
import SmallAlert from "../../../components/ui/alerts/SmallAlert";
import SmartButton from "../../../components/ui/buttons/SmartButton";
import TablaValidateLicense from "../../../components/ui/table/TablaValidateLicense";

const ValidateLicenseSection = () => {
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showConfirm, setShowConfirm] = useState(false);
  const [selectedProvider, setSelectedProvider] = useState(null);
  const [selectedDecision, setSelectedDecision] = useState(null);
  const [comment, setComment] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [toast, setToast] = useState({ show: false, type: "", message: "" });
  const [modalError, setModalError] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await fetchPendingLicenses();
      setRows(Array.isArray(data) ? data : []);
    } catch (e) {
      console.error("Error loading licenses:", e);
      if (e.message.includes("401") || e.message.includes("403")) {
        // Authentication error - redirect to login
        navigate("/custom-admin", { replace: true });
        return;
      }
      setError("Failed to load pending licenses");
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  useEffect(() => {
    load();
  }, [load]);

  // Set default comment when modal opens
  useEffect(() => {
    if (showConfirm && selectedDecision === "Approved") {
      setComment("Please continue with the next steps of the process.");
    }
  }, [showConfirm, selectedDecision]);

  const onDecision = (provider, decision, license) => {
    setSelectedProvider({ ...provider, license });
    setSelectedDecision(decision);
    setComment("");
    setModalError("");
    setShowConfirm(true);
  };

  const confirmDecision = async () => {
    if (!selectedProvider || !selectedDecision) return;

    // Clear any previous modal errors
    setModalError("");

    // Validate comment is not empty
    if (!comment || comment.trim() === "") {
      setModalError("Comment is required");
      return;
    }

    setSubmitting(true);
    try {
      await decideLicense(selectedProvider.id, {
        decision: selectedDecision,
        comment: comment.trim(),
      });
      setShowConfirm(false);
      setSelectedProvider(null);
      setSelectedDecision(null);
      setComment("");
      setModalError("");
      await load();
      setToast({
        show: true,
        type: "success",
        message: `License ${selectedDecision.toLowerCase()} successfully`,
      });
    } catch (e) {
      setModalError("Failed to update license decision");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading)
    return (
      <div className="note info">
        <b>Loading pending licenses...</b>
      </div>
    );
  if (error)
    return (
      <div className="note error">
        <b>Error loading licenses: {error}</b>
      </div>
    );
  return (
    <>
      <TablaValidateLicense rows={rows} onDecision={onDecision} />

      {showConfirm && (
        <ModalNew>
          <PopupHeader
            title={`Confirm License ${
              selectedDecision === "Approved" ? "Acceptation" : "Rejection"
            }`}
            setOpenState={setShowConfirm}
          />
          <div className="validate-license-modal-body">
            {modalError && (
              <div className="modal-alert-container">
                <SmallAlert alert="True" type="error" message={modalError} />
              </div>
            )}

            <div className="mb-16">
              <h4>Provider Information:</h4>
              <p>
                <strong>Name:</strong> {selectedProvider?.first_name}{" "}
                {selectedProvider?.last_name}
              </p>
              <p>
                <strong>Email:</strong> {selectedProvider?.email}
              </p>
              <p>
                <strong>Phone:</strong> {selectedProvider?.phone}
              </p>
              <p>
                <strong>License Number:</strong>{" "}
                {selectedProvider?.license || "N/A"}
              </p>
            </div>

            <div className="inputText validate-license-inputText-spacing">
              <textarea
                rows="4"
                value={comment}
                onChange={(e) => setComment(e.target.value)}
                disabled={submitting}
                maxLength={500}
              />
              <label>Comment (required)</label>
              <small className="character-count">
                {comment.length}/500 characters
              </small>
            </div>

            <div className="validate-license-actions">
              <SmartButton
                text={`Confirm ${
                  selectedDecision === "Approved" ? "Acceptation" : "Rejection"
                }`}
                icon={
                  selectedDecision === "Approved" ? "check_circle" : "close"
                }
                onClick={confirmDecision}
                className={
                  selectedDecision === "Approved" ? "btnAccept" : "btnReject"
                }
                disabled={submitting}
                isSubmitting={submitting}
                loadingText="Processing..."
              />
            </div>
          </div>
        </ModalNew>
      )}

      {/* Toast Notification */}
      {toast.show && (
        <div className="validate-license-toast">
          <SmallAlert
            alert="True"
            title={toast.type === "success" ? "Success" : "Error"}
            type={toast.type}
            message={toast.message}
            dismissOnClickOutside={true}
            dismissOnFocusOutside={false}
            onDismiss={() => setToast({ show: false, type: "", message: "" })}
          />
        </div>
      )}
    </>
  );
};

export default ValidateLicenseSection;
